/**
 * Sprint 25 Wave A PR-A1 — admin post-logout bounce target.
 *
 * The Admin Console's logout flow redirects through Keycloak's
 * end-session endpoint. Keycloak validates `post_logout_redirect_uri`
 * against the OIDC client allow-list in the realm; on staging the
 * allow-list contains only the customer-app origin (`/`), not
 * `/admin/`. Sprint 24.1 PR-H1 tried to send `/admin/` directly and
 * was rejected with "Invalid redirect uri". Sprint 25 Wave A is
 * explicitly forbidden from changing Keycloak realm config.
 *
 * Workaround: the Admin Console drops a marker in `localStorage`
 * before initiating the KC redirect (see `apps/admin/src/components/
 * AccountMenu.tsx`). The customer app reads it on mount; if present,
 * the customer app immediately re-redirects to `/admin/`, bouncing
 * the operator back to the Admin Console login flow without any
 * server / KC config change.
 *
 * Hardening:
 *   - The marker is single-use: it is removed from `localStorage`
 *     before the redirect to prevent loops if the customer app fails
 *     to load `/admin/` for any reason.
 *   - The redirect target is validated to be a same-origin path —
 *     no scheme, no `//`, must start with `/`. This prevents the
 *     marker from being abused as an open-redirect vector by anyone
 *     who can write to the operator's `localStorage`.
 *   - On the server side, no API change is required.
 */

const KEY = "gt:post-logout-target" as const;

/**
 * Read the post-logout target (if any), validate it as a same-origin
 * path, clear the storage entry, and return the validated target.
 * Pure, side-effecting on `localStorage` — call once on app mount.
 *
 * Returns `null` if:
 *   - `localStorage` is unavailable (SSR / private mode),
 *   - the marker is absent,
 *   - the marker fails the same-origin path validation.
 */
export function consumePostLogoutTarget(): string | null {
  if (typeof window === "undefined") return null;
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  // Single-use: clear before validating so a malformed entry doesn't
  // wedge subsequent loads.
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
  return validatePostLogoutTarget(raw);
}

/**
 * Pure validator: accept a same-origin path or a same-origin URL,
 * normalize to a same-origin path. Reject anything that could be an
 * open redirect.
 *
 * Exported for testing.
 */
export function validatePostLogoutTarget(raw: string): string | null {
  if (typeof raw !== "string" || raw.length === 0) return null;
  // Reject protocol-relative URLs (`//evil.example/...`).
  if (raw.startsWith("//")) return null;
  // Same-origin path: must start with `/`, must not contain `://`,
  // must not contain `\` or whitespace.
  if (raw.startsWith("/")) {
    if (/[\s\\]|:\/\//.test(raw)) return null;
    return raw;
  }
  // Absolute URL: only accept if origin equals current origin and
  // path starts with `/`.
  if (typeof window === "undefined") return null;
  try {
    const parsed = new URL(raw, window.location.origin);
    if (parsed.origin !== window.location.origin) return null;
    if (!parsed.pathname.startsWith("/")) return null;
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return null;
  }
}

export const POST_LOGOUT_TARGET_KEY = KEY;
